/**
 * The recurrence + money maths, reverse-engineered from the Notion formulas.
 *
 * These two lines are the contract the whole app rests on. If they change,
 * every number in the dashboard changes. See finance.test.ts for the golden
 * values pulled from the real Notion database.
 */

import type {
  CspBucket, DerivedCategory, Expense, NetWorthSnapshot, Recurrence,
} from './types.js';

/** 365.25 / 12 — average days per month, accounting for leap years. */
export const DAYS_PER_MONTH = 30.4375;

/**
 * How many times per month a recurrence fires.
 *
 *   Monthly       (freq 1,    mult 30.41667) → 1.0
 *   Weekly        (freq 7,    mult 1)        → 4.348
 *   Bi-Weekly     (freq 14,   mult 1)        → 2.174
 *   Semi-Monthly  (freq 0.5,  mult 30.41667) → 2.0
 *   Yearly        (freq 1,    mult 365)      → 0.0834
 *   Every 2 Years (freq 2,    mult 365)      → 0.0417
 */
export function perMonth(frequency: number, multiplierDays: number): number {
  const denom = frequency * multiplierDays;
  if (!denom || !Number.isFinite(denom)) return 0;
  return DAYS_PER_MONTH / denom;
}

/** "One Time" has no recurring monthly cost. */
export function perMonthFor(r: Recurrence | null | undefined): number {
  if (!r) return 1; // unknown → assume monthly rather than silently zeroing
  if (r.name === 'One Time') return 0;
  return r.perMonth;
}

/** amount × tax × units ÷ max(1, split) × perMonth */
export function monthlyAmount(e: Expense, r: Recurrence | null | undefined): number {
  const amount = Number(e.amount) || 0;
  const tax = Number(e.tax) || 1;
  const units = Number(e.units) || 1;
  const split = Math.max(1, Number(e.split) || 1);
  return ((amount * tax * units) / split) * perMonthFor(r);
}

export function yearlyAmount(e: Expense, r: Recurrence | null | undefined): number {
  return monthlyAmount(e, r) * 12;
}

/** assets + investments + savings + emergencyFund − debt */
export function totalNetWorth(s: NetWorthSnapshot): number {
  return s.assets + s.investments + s.savings + s.emergencyFund - s.debt;
}

/**
 * The share of guilt-free spending the first two runway tiers are sized against.
 *
 * Notion's "Target Emergency Fund" database is the source of truth: its
 * `someGuiltFreePercentage` is 0.5 and its note reads "Target amount for FEX and
 * FEX + INV includes 50% of Guilt Free spending amount." The reasoning is sound —
 * in a real emergency you cut discretionary spending hard, but not to zero. A
 * runway priced at bare fixed costs assumes you stop eating out for a year.
 */
export const EF_SOME_GUILT_FREE = 0.5;

export type EfTierKey = 'fex' | 'fexInv' | 'fexInvGfs';

export interface EfTier {
  key: EfTierKey;
  /** short column label, matching the Notion field names */
  label: string;
  /** what the tier actually covers, spelled out */
  detail: string;
  /** monthly burn this tier is sized against */
  burn: number;
}

/**
 * The three runway tiers, derived from Notion's `toFund*` columns.
 *
 * Reproduced exactly (verified against the live database 2026-07-29):
 *
 *   FEXSGFSPerMonth   = fixed + 0.5 × gfs
 *   toFundFEX         = FEXSGFSPerMonth               × months
 *   toFundFEXINV      = FEXSGFSPerMonth + investments × months
 *   toFundFEXINVGFS   = fixed + gfs + investments     × months
 *
 * Note the third tier swaps the half-share for the *whole* guilt-free budget
 * rather than adding another half on top — that's what makes it "cover
 * everything", and it's why the jump from tier 2 to tier 3 is only half of GFS.
 *
 * One deliberate divergence: Notion's `is*Funded` percentages each divide by a
 * base one guilt-free step behind their own dollar target (`isFEXFunded` uses
 * fixed alone against a target of fixed + 50% gfs, and so on down the row). That
 * makes its bars disagree with its own dollar figures. Each tier here uses the
 * same burn for both, so a percentage always refers to the amount beside it.
 *
 * Payroll deductions are absent throughout, because `byBucket` excludes them —
 * correct here for the usual reason: a 401K contribution stops the moment the
 * paycheque does, so the fund never has to cover it.
 *
 * That is the one place the investments tier deliberately parts company with
 * Notion, whose `investmentsPerMonth` is $844.24 = 401K ($669.24) + Stocks
 * ($175). Its `investments` relation links only 2 of the 4 rows in the
 * Investments database — it includes the payroll-deducted 401K and omits the Roth
 * IRA entirely, which looks like a relation that was set before the Roth row
 * existed. Ours is the invest bucket as synced (Roth IRA + Stocks), which both
 * tracks new rows automatically and excludes the contribution that stops with
 * the job. Expect our tier-2 and tier-3 targets to sit ~$120 below Notion's at
 * three months for that reason.
 */
export function emergencyFundTiers(byBucket: Record<CspBucket, number>): EfTier[] {
  const { fixed, gfs, invest } = byBucket;
  const some = gfs * EF_SOME_GUILT_FREE;
  return [
    {
      key: 'fex',
      // Notion calls this FEXSGFS — fixed expenses plus *some* guilt-free. The
      // "S" is load-bearing: labelling it plain FEX would hide the half-share of
      // discretionary spending baked into the target.
      label: 'FEXSGFS',
      detail: `Fixed costs + ${Math.round(EF_SOME_GUILT_FREE * 100)}% guilt-free`,
      burn: fixed + some,
    },
    {
      key: 'fexInv',
      label: 'FEXSGFS + INV',
      detail: `Fixed costs + ${Math.round(EF_SOME_GUILT_FREE * 100)}% guilt-free + investments`,
      burn: fixed + some + invest,
    },
    {
      key: 'fexInvGfs',
      // Bare FEX here, because this tier carries the *whole* guilt-free budget
      // as its own term rather than the half-share.
      label: 'FEX + INV + GFS',
      detail: 'Fixed costs + investments + all guilt-free',
      burn: fixed + gfs + invest,
    },
  ];
}

/** How many months of a given monthly burn the fund covers. */
export function monthsCovered(emergencyFund: number, monthlyBurn: number): number {
  if (monthlyBurn <= 0) return 0;
  return emergencyFund / monthlyBurn;
}

/** Progress toward an N-month runway at a given burn rate, capped at 100. */
export function emergencyFundPct(
  emergencyFund: number,
  monthlyBurn: number,
  months: number,
): number {
  const target = monthlyBurn * months;
  if (target <= 0) return 0;
  return Math.min(100, (emergencyFund / target) * 100);
}

/** (1 − net/gross) × 100 — informational only. */
export function effectiveTaxPct(netMonthly: number, grossMonthly: number): number {
  if (grossMonthly <= 0) return 0;
  return (1 - netMonthly / grossMonthly) * 100;
}

// ─────────────────────── the cash-flow split ───────────────────────

/**
 * How one month of gross pay divides up.
 *
 * The single place that answers "what comes out of take-home?", because getting
 * two different answers in two different views is exactly what went wrong
 * before: the Dashboard and the Sankey each summed every group into a bucket
 * total, including the payroll deductions, so committed money exceeded take-home
 * by the size of the 401K and HSA contributions and every downstream percentage
 * was computed off the inflated figure.
 *
 * Gross splits four ways — tax, pre-tax deductions, post-tax deductions, and the
 * take-home that actually lands in the account. Only take-home is available to
 * the CSP buckets, so only groups with `deduction === null` are charged against
 * it. `tax` is what's left of gross once take-home and both deduction totals are
 * accounted for, which keeps the four shares summing to gross by construction
 * rather than by hope.
 */
export interface CashFlowSplit {
  gross: number;
  /** take-home that reaches the account, after tax and all payroll deductions */
  net: number;
  /** gross − net − preTax − postTax */
  tax: number;
  preTax: number;
  postTax: number;
  /** take-home outflows only, by bucket */
  byBucket: Record<CspBucket, number>;
  /** sum of byBucket — money take-home is already promised to */
  committed: number;
  /** net − committed, floored at 0 */
  unallocated: number;
  /** committed − net, floored at 0 — non-zero means the plan overruns take-home */
  overAllocated: number;
}

export function cashFlowSplit(
  cats: DerivedCategory[],
  net: number,
  gross: number,
): CashFlowSplit {
  const byBucket: Record<CspBucket, number> = { fixed: 0, invest: 0, savings: 0, gfs: 0 };
  let preTax = 0;
  let postTax = 0;

  for (const c of cats) {
    if (c.deduction === 'pre') preTax += c.base;
    else if (c.deduction === 'post') postTax += c.base;
    else byBucket[c.bucket] += c.base;
  }

  const committed = Object.values(byBucket).reduce((a, b) => a + b, 0);
  // Deductions are already inside the gross-to-net gap; tax is the remainder.
  const tax = Math.max(0, gross - net - preTax - postTax);

  return {
    gross,
    net,
    tax,
    preTax,
    postTax,
    byBucket,
    committed,
    unallocated: Math.max(0, net - committed),
    overAllocated: Math.max(0, committed - net),
  };
}

/** Take-home groups only — what the budget meter and auto-balance may dial. */
export const takeHomeCats = (cats: DerivedCategory[]): DerivedCategory[] =>
  cats.filter(c => c.deduction === null);

/** Payroll-deduction groups — real money, but not drawn from take-home. */
export const deductionCats = (cats: DerivedCategory[]): DerivedCategory[] =>
  cats.filter(c => c.deduction !== null);

// ─────────────────────────── formatting ───────────────────────────

export function fmtMoney(n: number, dp = 0): string {
  const v = Number.isFinite(n) ? n : 0;
  const sign = v < 0 ? '−' : '';
  return (
    sign +
    '$' +
    Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })
  );
}

export function fmtMoneyK(n: number): string {
  const v = Number.isFinite(n) ? n : 0;
  return (v < 0 ? '−' : '') + '$' + Math.round(Math.abs(v) / 1000) + 'k';
}

export function fmtPct(n: number, dp = 1): string {
  return Number.isFinite(n) ? `${n.toFixed(dp)}%` : '—';
}
